// ============================================================
// systems/frozen.js — Frozen / lightning / iceberg system (1500+)
// ============================================================
import { state } from '../state.js';
import { dom } from '../dom.js';
import { stopFireEffect, startFireEffect } from './fire.js';
import { applyHazardDamage } from './competitive.js';
import { checkPhaseDowngrades, registerDeactivator } from './bombs.js';
import { startMusic } from '../audio/musicChill.js';
import { startOgMusic } from '../audio/musicOg.js';

// ---- Helpers ----

function startCurrentMusic() {
  if (state.musicSource === 'mp3') { startMusic(); }
  else { startOgMusic(); }
}

function distToSegment(px, py, x1, y1, x2, y2) {
  const dx = x2 - x1, dy = y2 - y1;
  const lenSq = dx * dx + dy * dy;
  if (lenSq === 0) return Math.hypot(px - x1, py - y1);
  let t = ((px - x1) * dx + (py - y1) * dy) / lenSq;
  t = Math.max(0, Math.min(1, t));
  return Math.hypot(px - (x1 + t * dx), py - (y1 + t * dy));
}

// ---- Public API ----

export function activateFrozenMode() {
  if (state.frozenMode) return;
  state.frozenMode = true;

  // Frozen plushie
  dom.smiley.classList.add('frozen');
  dom.smiley.classList.remove('burning');
  stopFireEffect();

  // Cold background
  document.body.classList.remove('danger-mode');
  document.body.classList.add('frozen-mode');

  // Warning
  dom.frozenWarningEl.classList.add('show');
  setTimeout(() => dom.frozenWarningEl.classList.remove('show'), 3000);

  // Ice particles on the plushie
  state.iceInterval = setInterval(() => {
    if (state.phase3Active && Math.random() > 0.3) return; // reduce in phase 3
    const ice = document.createElement('div');
    ice.className = 'ice-particle';
    const size = 3 + Math.random() * 5;
    ice.style.width = size + 'px';
    ice.style.height = size * 1.5 + 'px';
    ice.style.left = (Math.random() * 160) + 'px';
    ice.style.top = (Math.random() * 20) + 'px';
    ice.style.animationDuration = (0.6 + Math.random() * 0.8) + 's';
    dom.fireWrap.appendChild(ice);
    setTimeout(() => ice.remove(), 1400);
  }, 120);

  // Freeze sound
  if (state.audioCtx) {
    const now = state.audioCtx.currentTime;
    const osc = state.audioCtx.createOscillator();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(1200, now);
    osc.frequency.linearRampToValueAtTime(200, now + 0.8);
    const env = state.audioCtx.createGain();
    env.gain.setValueAtTime(0.12, now);
    env.gain.exponentialRampToValueAtTime(0.001, now + 1);
    osc.connect(env).connect(state.audioCtx.destination);
    osc.start(now);
    osc.stop(now + 1);
  }

  // Start lightning strikes
  state.lightningInterval = setInterval(fireLightning, 2500 + Math.random() * 2000);

  // Start spawning icebergs
  spawnIceberg();
  state.icebergInterval = setInterval(spawnIceberg, 1800 + Math.random() * 1200);
  state.icebergAnimFrame = requestAnimationFrame(updateIcebergs);
}

export function spawnIceberg() {
  if (!state.frozenMode || state.paused) return;
  const el = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  el.setAttribute('class', 'iceberg');
  el.setAttribute('width', '45');
  el.setAttribute('height', '45');
  el.setAttribute('viewBox', '0 0 64 64');
  // Random iceberg shape
  const shapes = [
    `<polygon points="32,4 52,28 58,48 42,60 22,60 6,48 12,28" fill="#b8dced" stroke="#8ab8d0" stroke-width="2"/>
     <polygon points="32,4 40,22 52,28 32,20 12,28 24,22" fill="#d4eef8" stroke="none"/>
     <line x1="20" y1="35" x2="44" y2="35" stroke="#9cc8db" stroke-width="1"/>
     <line x1="25" y1="45" x2="40" y2="45" stroke="#9cc8db" stroke-width="1"/>`,
    `<polygon points="28,2 48,18 56,40 50,58 14,58 8,40 16,18" fill="#a8d4e8" stroke="#7cb8d4" stroke-width="2"/>
     <polygon points="28,2 38,16 48,18 28,14 16,18 24,16" fill="#cce8f4" stroke="none"/>
     <line x1="18" y1="32" x2="46" y2="32" stroke="#8cc0d8" stroke-width="1"/>
     <line x1="22" y1="48" x2="42" y2="48" stroke="#8cc0d8" stroke-width="1"/>`,
    `<polygon points="20,6 44,6 58,30 50,56 14,56 6,30" fill="#c0e0f0" stroke="#90c0d8" stroke-width="2"/>
     <polygon points="20,6 44,6 36,20 28,20" fill="#dceef8" stroke="none"/>
     <line x1="16" y1="38" x2="48" y2="38" stroke="#a0d0e4" stroke-width="1"/>`
  ];
  el.innerHTML = shapes[Math.floor(Math.random() * shapes.length)];

  const edge = Math.floor(Math.random() * 4);
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  let x, y;
  const speed = 0.8 + Math.random() * 1.2;

  if (edge === 0) { x = Math.random() * vw; y = -55; }
  else if (edge === 1) { x = Math.random() * vw; y = vh + 55; }
  else if (edge === 2) { x = -55; y = Math.random() * vh; }
  else { x = vw + 55; y = Math.random() * vh; }

  const targetX = vw / 2 + (Math.random() - 0.5) * vw * 0.6;
  const targetY = vh / 2 + (Math.random() - 0.5) * vh * 0.6;
  const angle = Math.atan2(targetY - y, targetX - x);
  const vx = Math.cos(angle) * speed;
  const vy = Math.sin(angle) * speed;
  const rot = Math.random() * 360;
  const rotSpeed = (Math.random() - 0.5) * 2;

  el.style.transform = `translate(${x}px, ${y}px) rotate(0deg)`;
  document.body.appendChild(el);
  state.icebergs.push({ el, x, y, vx, vy, rot, rotSpeed });
}

export function updateIcebergs() {
  if (!state.frozenMode) return;
  if (state.paused) { state.icebergAnimFrame = requestAnimationFrame(updateIcebergs); return; }

  for (let i = state.icebergs.length - 1; i >= 0; i--) {
    const b = state.icebergs[i];
    b.x += b.vx;
    b.y += b.vy;
    b.rot += b.rotSpeed;
    b.el.style.transform = `translate(${b.x}px, ${b.y}px) rotate(${b.rot}deg)`;

    // Check cursor collision
    const cx = b.x + 22;
    const cy = b.y + 22;
    const d = Math.hypot(state.mouseX - cx, state.mouseY - cy);
    if (d < 30) {
      applyHazardDamage(50);

      // Shatter effect
      const exp = document.createElement('div');
      exp.className = 'particle';
      exp.textContent = '-50 \u2744';
      exp.style.left = b.x + 'px';
      exp.style.top = b.y + 'px';
      exp.style.color = '#4dc9f6';
      exp.style.fontSize = '30px';
      document.body.appendChild(exp);
      setTimeout(() => exp.remove(), 800);

      // Crackle sound
      if (state.audioCtx) {
        const now = state.audioCtx.currentTime;
        const noise = state.audioCtx.createBufferSource();
        const buf = state.audioCtx.createBuffer(1, state.audioCtx.sampleRate * 0.12, state.audioCtx.sampleRate);
        const nd = buf.getChannelData(0);
        for (let j = 0; j < nd.length; j++) {
          nd[j] = (Math.random() * 2 - 1) * Math.pow(1 - j / nd.length, 3);
        }
        noise.buffer = buf;
        const env = state.audioCtx.createGain();
        env.gain.setValueAtTime(0.15, now);
        env.gain.exponentialRampToValueAtTime(0.001, now + 0.12);
        const hp = state.audioCtx.createBiquadFilter();
        hp.type = 'highpass';
        hp.frequency.value = 3000;
        noise.connect(hp).connect(env).connect(state.audioCtx.destination);
        noise.start(now);
        noise.stop(now + 0.15);
      }

      document.body.style.boxShadow = 'inset 0 0 60px rgba(100,200,255,0.3)';
      setTimeout(() => document.body.style.boxShadow = '', 150);

      b.el.remove();
      state.icebergs.splice(i, 1);

      checkPhaseDowngrades();
      continue;
    }

    // Remove if off screen
    if (b.x < -150 || b.x > window.innerWidth + 150 ||
        b.y < -150 || b.y > window.innerHeight + 150) {
      b.el.remove();
      state.icebergs.splice(i, 1);
    }
  }
  state.icebergAnimFrame = requestAnimationFrame(updateIcebergs);
}

export function deactivateFrozenMode() {
  state.frozenMode = false;
  dom.smiley.classList.remove('frozen');
  document.body.classList.remove('frozen-mode');
  clearInterval(state.lightningInterval);
  clearInterval(state.iceInterval);
  clearInterval(state.icebergInterval);
  cancelAnimationFrame(state.icebergAnimFrame);
  state.icebergs.forEach(b => b.el.remove());
  state.icebergs = [];
  dom.fireWrap.innerHTML = '';

  // Restore to burn mode since we're still >= 500
  if (state.bombMode) {
    dom.smiley.classList.add('burning');
    startFireEffect();
    document.body.classList.add('danger-mode');
  }
}

export function fireLightning() {
  if (!state.frozenMode || state.paused) return;

  const vw = window.innerWidth;
  const vh = window.innerHeight;

  // Pick a random side: 0=top, 1=bottom, 2=left, 3=right
  const side = Math.floor(Math.random() * 4);
  let startX, startY, endX, endY, warnEl;

  if (side === 0) { // top
    startX = Math.random() * vw; startY = 0;
    endX = startX + (Math.random() - 0.5) * 200; endY = vh;
  } else if (side === 1) { // bottom
    startX = Math.random() * vw; startY = vh;
    endX = startX + (Math.random() - 0.5) * 200; endY = 0;
  } else if (side === 2) { // left
    startX = 0; startY = Math.random() * vh;
    endX = vw; endY = startY + (Math.random() - 0.5) * 200;
  } else { // right
    startX = vw; startY = Math.random() * vh;
    endX = 0; endY = startY + (Math.random() - 0.5) * 200;
  }

  // Show warning line first
  warnEl = document.createElement('div');
  warnEl.className = 'lightning-warn';
  if (side <= 1) {
    // Vertical lightning
    warnEl.style.left = startX + 'px';
    warnEl.style.top = '0';
    warnEl.style.width = '3px';
    warnEl.style.height = '100vh';
    warnEl.classList.add('vertical');
  } else {
    // Horizontal lightning
    warnEl.style.left = '0';
    warnEl.style.top = startY + 'px';
    warnEl.style.width = '100vw';
    warnEl.style.height = '3px';
  }
  document.body.appendChild(warnEl);

  // Warning sound
  if (state.audioCtx) {
    const now = state.audioCtx.currentTime;
    for (let i = 0; i < 3; i++) {
      const osc = state.audioCtx.createOscillator();
      osc.type = 'square';
      osc.frequency.value = 800 + i * 200;
      const env = state.audioCtx.createGain();
      env.gain.setValueAtTime(0.04, now + i * 0.12);
      env.gain.exponentialRampToValueAtTime(0.001, now + i * 0.12 + 0.1);
      osc.connect(env).connect(state.audioCtx.destination);
      osc.start(now + i * 0.12);
      osc.stop(now + i * 0.12 + 0.12);
    }
  }

  // Fire the actual lightning after warning
  setTimeout(() => {
    warnEl.remove();
    if (!state.frozenMode) return;

    // Create lightning bolt SVG with jagged path
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('class', 'lightning-bolt flash');
    svg.style.left = '0';
    svg.style.top = '0';
    svg.style.width = '100vw';
    svg.style.height = '100vh';

    const segments = 8 + Math.floor(Math.random() * 6);
    let prevX = startX, prevY = startY;
    let lines = '';
    for (let i = 1; i <= segments; i++) {
      const t = i / segments;
      let nx = startX + (endX - startX) * t + (Math.random() - 0.5) * 80;
      let ny = startY + (endY - startY) * t + (Math.random() - 0.5) * 80;
      if (i === segments) { nx = endX; ny = endY; }
      lines += `<line x1="${prevX}" y1="${prevY}" x2="${nx}" y2="${ny}"/>`;
      prevX = nx; prevY = ny;
    }
    svg.innerHTML = lines;
    document.body.appendChild(svg);

    // Screen flash
    document.body.classList.add('lightning-flash');
    setTimeout(() => document.body.classList.remove('lightning-flash'), 150);

    // Thunder sound
    if (state.audioCtx) {
      const now = state.audioCtx.currentTime;
      const noise = state.audioCtx.createBufferSource();
      const buf = state.audioCtx.createBuffer(1, state.audioCtx.sampleRate * 0.5, state.audioCtx.sampleRate);
      const d = buf.getChannelData(0);
      for (let i = 0; i < d.length; i++) {
        d[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / d.length, 1.2);
      }
      noise.buffer = buf;
      const env = state.audioCtx.createGain();
      env.gain.setValueAtTime(0.25, now);
      env.gain.exponentialRampToValueAtTime(0.001, now + 0.5);
      const lp = state.audioCtx.createBiquadFilter();
      lp.type = 'lowpass';
      lp.frequency.value = 800;
      noise.connect(lp).connect(env).connect(state.audioCtx.destination);
      noise.start(now);
      noise.stop(now + 0.6);

      // High crack
      const crack = state.audioCtx.createOscillator();
      crack.type = 'sawtooth';
      crack.frequency.value = 3000;
      const crackEnv = state.audioCtx.createGain();
      crackEnv.gain.setValueAtTime(0.08, now);
      crackEnv.gain.exponentialRampToValueAtTime(0.001, now + 0.08);
      crack.connect(crackEnv).connect(state.audioCtx.destination);
      crack.start(now);
      crack.stop(now + 0.1);
    }

    // Check if cursor is near the lightning path — subtract 100 clicks
    const dist = distToSegment(state.mouseX, state.mouseY, startX, startY, endX, endY);
    if (dist < 40) {
      applyHazardDamage(100);

      const dmg = document.createElement('div');
      dmg.className = 'particle';
      dmg.textContent = '-100 \u26A1';
      dmg.style.left = state.mouseX + 'px';
      dmg.style.top = state.mouseY + 'px';
      dmg.style.color = '#4dc9f6';
      dmg.style.fontSize = '32px';
      document.body.appendChild(dmg);
      setTimeout(() => dmg.remove(), 800);

      document.body.style.boxShadow = 'inset 0 0 80px rgba(100,200,255,0.5)';
      setTimeout(() => document.body.style.boxShadow = '', 200);

      checkPhaseDowngrades();
    }

    // Remove lightning bolt after flash
    setTimeout(() => svg.remove(), 400);
  }, 800);
}

// Register frozen deactivator so bombs.js can call it without circular import
registerDeactivator('frozen', deactivateFrozenMode);
